// LUMINA · pages/vajra/components/FeaturedCarousel.jsx
// Phase 99.1: 数据驱动轮播 — 接收 API events 构建 slides，硬编码降级为 fallback
// 解决: Carousel 硬编码与 featured retreat cards 的结构性重复风险

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { GD, GDH, EVENT_TYPE_LABEL, EVENT_TYPE_COLOR, TZ_LABELS } from '../constants';


// ── 事件类型 → 默认图片映射（events 无 gallery 时使用）────────────
const TYPE_DEFAULT_IMAGE = {
  retreat: "/images/Tara2.jpg",
  empowerment: "/images/KalachakraMandalaLO-220801-190044.jpg",
  teaching: "/images/Naropa.jpg",
  puja: "/images/黄度母.jpg",
  weekly_practice: "/images/Naropa.jpg",
};

// ── 事件类型 → 感性 CTA 文案（slider 用呼吁行动语，区别于列表的具象信息）─
const TYPE_CTA = {
  zh: {
    retreat: "加入闭关，圆满顺缘",
    empowerment: "领受灌顶，种下解脱种子",
    teaching: "听闻正法，开启智慧之门",
    weekly_practice: "每周共修，汇聚善缘",
    puja: "共赴法会，同沾法喜",
    ritual: "祈请加持，净除障碍",
    default: "珍惜法缘，莫失良机",
  },
  en: {
    retreat: "Join the retreat, perfect the conditions",
    empowerment: "Receive empowerment, plant the seed of liberation",
    teaching: "Hear the Dharma, open the door of wisdom",
    weekly_practice: "Weekly practice, gather auspicious connections",
    puja: "Join the puja, share the joy of Dharma",
    ritual: "Invoke blessings, clear away obstacles",
    default: "Treasure this opportunity",
  },
};

// ── 从 event 对象构建 carousel slide ────────────────────────────
function buildSlide(ev, lang) {
  // Image: gallery[0] > cover_image_url > type default > static fallback
  let image = "/images/Tara2.jpg";  // default fallback image
  let galleryImgs = ev.gallery_images;
  if (typeof galleryImgs === 'string') {
    try { galleryImgs = JSON.parse(galleryImgs); } catch { galleryImgs = null; }
  }
  if (Array.isArray(galleryImgs) && galleryImgs.length > 0) {
    image = galleryImgs[0];
  } else if (ev.cover_image_url) {
    image = ev.cover_image_url;
  } else if (TYPE_DEFAULT_IMAGE[ev.event_type]) {
    image = TYPE_DEFAULT_IMAGE[ev.event_type];
  }

  // Title
  const title = (lang === 'en' && ev.title_en) ? ev.title_en : (ev.title_zh || ev.title || '');
  
  // Date line
  let dateLine = '';
  if (ev.start_date) {
    dateLine = ev.start_date;
    if (ev.end_date && ev.end_date !== ev.start_date) dateLine += ` — ${ev.end_date}`;
    if (ev.timezone) dateLine += ` ${TZ_LABELS[ev.timezone] || ev.timezone}`;
  }

  // Emotional CTA subtitle (differentiated from the factual list below)
  const ctaMap = TYPE_CTA[lang] || TYPE_CTA.zh;
  const subtitle = ctaMap[ev.event_type] || ctaMap.default;

  // Route: series slug > event slug > calendar fallback
  const link = ev.series_slug ? `/vajra/special/${ev.series_slug}`
    : ev.slug ? `/events/${ev.slug}`
    : "/vajra/calendar";

  // Event type label for badge
  const typeLabel = EVENT_TYPE_LABEL[ev.event_type] || '';
  const typeColor = EVENT_TYPE_COLOR[ev.event_type] || GD;

  // Description (first available)
  const desc = ev.description_zh || ev.description_en || ev.description || '';

  return { image, title, subtitle, dateLine, desc, link, typeLabel, typeColor, isEvent: true, event: ev };
}

const AUTOPLAY_MS = 6000;

export default function FeaturedCarousel({ featuredEvents, lang = "zh" }) {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);

  // Build slides: events-driven if data exists, otherwise fallback
  const slides = useMemo(() => {
    if (featuredEvents && featuredEvents.length > 0) {
      return featuredEvents.slice(0, 5).map(ev => buildSlide(ev, lang));
    }
    return [];  // no featured events → no carousel slides
  }, [featuredEvents, lang]);

  const next = useCallback(() => {
    setCurrent(prev => (prev + 1) % slides.length);
  }, [slides.length]);

  const prev = () => {
    setCurrent(p => (p - 1 + slides.length) % slides.length);
  };

  // Autoplay
  useEffect(() => {
    if (slides.length <= 1) return;
    const t = setInterval(next, AUTOPLAY_MS);
    return () => clearInterval(t);
  }, [next, slides.length]);

  // Reset current if slides array changes (e.g. lang switch)
  useEffect(() => {
    if (current >= slides.length) setCurrent(0);
  }, [slides.length, current]);

  const s = slides[current] || slides[0];
  if (!s) return null;

  return (
    <div style={{
      position: "relative", width: "100%", height: "min(50vw, 420px)",
      overflow: "hidden", background: "#0a0a0a",
    }}>
      {/* Slides */}
      {slides.map((slide, i) => (
        <div key={i} style={{
          position: "absolute", inset: 0,
          backgroundImage: `url(${slide.image})`,
          backgroundSize: "cover", backgroundPosition: "center 30%",
          opacity: i === current ? 1 : 0,
          transition: "opacity 0.6s ease",
          zIndex: i === current ? 1 : 0,
        }} />
      ))}

      {/* Dark overlay */}
      <div style={{
        position: "absolute", inset: 0,
        background: "linear-gradient(135deg, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.30) 55%, rgba(0,0,0,0.55) 100%)",
        zIndex: 2,
      }} />

      {/* Text content */}
      <div style={{
        position: "absolute", bottom: "10%", left: "6%", right: "6%",
        zIndex: 3, color: "#fff",
      }}>
        {/* Event type badge (only for data-driven slides) */}
        {s.isEvent && s.typeLabel && (
          <span style={{
            display: "inline-block", fontSize: 10, letterSpacing: 2,
            padding: "3px 12px", borderRadius: 12, marginBottom: 10,
            background: `${s.typeColor}88`, border: `1px solid ${s.typeColor}aa`,
            textTransform: "uppercase",
          }}>
            {s.typeLabel}
          </span>
        )}

        {/* Emotional CTA subtitle */}
        <div style={{
          fontSize: "clamp(13px, 2.2vw, 20px)",
          letterSpacing: 4, opacity: 0.85,
          fontFamily: "'Noto Serif SC', serif",
          marginBottom: 8,
          textShadow: "0 2px 8px rgba(0,0,0,0.6)",
        }}>
          {s.subtitle}
        </div>

        {/* Title */}
        <div style={{
          fontSize: "clamp(22px, 4vw, 40px)",
          letterSpacing: 3, fontWeight: 600,
          fontFamily: "'Noto Serif SC', serif",
          marginBottom: 8,
          textShadow: "0 2px 12px rgba(0,0,0,0.6)",
        }}>
          {s.title}
        </div>

        {/* Date line (data-driven only) */}
        {s.isEvent && s.dateLine && (
          <div style={{
            fontSize: "clamp(11px, 1.5vw, 13px)",
            letterSpacing: 1.5, opacity: 0.7,
            marginBottom: 8,
            textShadow: "0 1px 4px rgba(0,0,0,0.5)",
          }}>
            📅 {s.dateLine}
          </div>
        )}

        {/* Description (data-driven only) */}
        {s.isEvent && s.desc && (
          <div style={{
            fontSize: "clamp(12px, 1.4vw, 14px)",
            lineHeight: 1.6, opacity: 0.8, maxWidth: "85%",
            marginBottom: 16,
            textShadow: "0 1px 4px rgba(0,0,0,0.5)",
          }}>
            {s.desc.length > 120 ? s.desc.slice(0, 120) + "…" : s.desc}
          </div>
        )}

        {/* CTA button */}
        <button onClick={() => navigate(s.link)} style={{
          background: `linear-gradient(135deg, ${GDH}, #C9A84C)`,
          border: "none", borderRadius: 10, cursor: "pointer",
          color: "#fff", fontSize: "clamp(12px, 1.5vw, 14px)",
          padding: "10px 28px", letterSpacing: 2,
          boxShadow: "0 4px 20px rgba(212,175,55,0.35)",
          transition: "transform 0.2s, box-shadow 0.2s",
        }}
        onMouseOver={e => {
          e.currentTarget.style.transform = "translateY(-2px)";
          e.currentTarget.style.boxShadow = "0 6px 28px rgba(212,175,55,0.5)";
        }}
        onMouseOut={e => {
          e.currentTarget.style.transform = "none";
          e.currentTarget.style.boxShadow = "0 4px 20px rgba(212,175,55,0.35)";
        }}
        >
          {lang === "zh" ? "了解详情 →" : "Learn More →"}
        </button>
      </div>

      {/* Arrows */}
      {slides.length > 1 && (
        <>
          <button onClick={prev} style={{
            position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)",
            zIndex: 4, background: "rgba(0,0,0,0.35)", border: "none",
            borderRadius: "50%", width: 40, height: 40, cursor: "pointer",
            color: "#fff", fontSize: 20, display: "flex", alignItems: "center",
            justifyContent: "center", transition: "background 0.2s",
          }}
          onMouseOver={e => e.currentTarget.style.background = "rgba(0,0,0,0.55)"}
          onMouseOut={e => e.currentTarget.style.background = "rgba(0,0,0,0.35)"}
          >‹</button>

          <button onClick={next} style={{
            position: "absolute", right: 16, top: "50%", transform: "translateY(-50%)",
            zIndex: 4, background: "rgba(0,0,0,0.35)", border: "none",
            borderRadius: "50%", width: 40, height: 40, cursor: "pointer",
            color: "#fff", fontSize: 20, display: "flex", alignItems: "center",
            justifyContent: "center", transition: "background 0.2s",
          }}
          onMouseOver={e => e.currentTarget.style.background = "rgba(0,0,0,0.55)"}
          onMouseOut={e => e.currentTarget.style.background = "rgba(0,0,0,0.35)"}
          >›</button>
        </>
      )}

      {/* Dots */}
      {slides.length > 1 && (
        <div style={{
          position: "absolute", bottom: 20, left: "50%", transform: "translateX(-50%)",
          zIndex: 4, display: "flex", gap: 8,
        }}>
          {slides.map((_, i) => (
            <button key={i} onClick={() => setCurrent(i)} style={{
              width: i === current ? 24 : 8, height: 8,
              borderRadius: 4, border: "none", cursor: "pointer",
              background: i === current ? GDH : "rgba(255,255,255,0.4)",
              transition: "all 0.3s ease",
            }} />
          ))}
        </div>
      )}
    </div>
  );
}
